// Export `{ ... }` attribute values to C. Design: eval_design.md +
// implementation_details.md §1.6 (under packages/lved-runtime/.../lv_xml/).
//
// Arithmetic / comparison / ternary map ~1:1 to C (consts resolve to their
// UPPERCASE macro, params/subjects stay bare). String `==`/`!=` -> lv_streq,
// color `==`/`!=` -> lv_color_eq. Pointer types (image/font/subject) and string
// truthiness (`!x`, `x ? a : b`) need no special handling — `!ptr` / `ptr ==
// ptr` work as plain C. String concatenation (`.`) compiles to an
// lv_snprintf into a reused buffer pool (`eval_buf_1`, …), returned as a
// `preface`; a concat may be a ternary branch, so a statement can need two.
//
// Concat operands are stringified to match the runtime: int `%d`, string `%s`,
// bool true/false, color `0xRRGGBB`, float `%.2f`.
//
// Import-light (types only) so the validation layer can reuse `isExpression`.

import type { ElementData, LVGLSchemaService, ConstDefinitionData, ArgSchema } from '../../../';

/** A `{ ... }` attribute value opts into expression evaluation. */
export const isExpression = (value: string): boolean => {
    const trimmed: string = value.trim();
    return trimmed.startsWith('{') && trimmed.endsWith('}') && trimmed.length >= 2;
};

// Buffer size macro, emitted (`#ifndef`-guarded) in the includes; overridable.
export const EVAL_STRING_BUF_SIZE_MACRO: string = 'LV_XML_EVAL_STRING_BUF_SIZE';
export const EVAL_STRING_BUF_SIZE_DEFAULT: number = 256;

// Buffer pool: the per-statement counter restarts each setter (names reused
// across statements); the high-water mark says how many to declare per function.
let _stmtBufCounter: number = 0;
let _maxBufCount: number = 0;

/** Restart the pool for the next statement (call per setter). */
export const resetExpressionBufferStatement = (): void => {
    _stmtBufCounter = 0;
};

/** Reset both counters; call once per generated file. */
export const resetExpressionBufferCounter = (): void => {
    _stmtBufCounter = 0;
    _maxBufCount = 0;
};

/** Pool buffers the current function needs. */
export const getExpressionBufferCount = (): number => _maxBufCount;

const nextBufName = (): string => {
    _stmtBufCounter++;
    if (_stmtBufCounter > _maxBufCount) _maxBufCount = _stmtBufCounter;
    return `eval_buf_${_stmtBufCounter}`;
};

const isConstName = (name: string, element: ElementData | undefined, schemaService: LVGLSchemaService): boolean =>
    Boolean(schemaService.getConstDefinition(name) || name in (element?.consts ?? {}));

// Exported for the grammar validator (expression_validate.ts), which lexes with the same rules.
export const isIdentifierStart = (ch: string): boolean => /[A-Za-z_]/.test(ch);
export const isIdentifierPart = (ch: string): boolean => /[A-Za-z0-9_]/.test(ch);
export const isDigit = (ch: string): boolean => /[0-9]/.test(ch);

type ExprContext = {
    element: ElementData | undefined;
    schemaService: LVGLSchemaService;
};

// Emit a scalar (non-concat) expression ~1:1: resolve const identifiers to their
// UPPERCASE macro and convert `'…'` literals to C strings; skip the insides of
// string/number literals so they aren't mistaken for identifiers.
const resolveScalar = (expr: string, ctx: ExprContext): string => {
    let out: string = '';
    let i: number = 0;

    while (i < expr.length) {
        const ch: string = expr[i];

        // Single-quoted string literal -> C double-quoted string
        if (ch === "'") {
            let j: number = i + 1;
            let body: string = '';
            while (j < expr.length && expr[j] !== "'") {
                if (expr[j] === '\\' && j + 1 < expr.length) {
                    body += expr[j] + expr[j + 1];
                    j += 2;
                    continue;
                }
                body += expr[j];
                j++;
            }
            out += '"' + body.replace(/"/g, '\\"') + '"';
            i = j + 1;
            continue;
        }

        // Numeric / hex literal (cannot start an identifier) -> verbatim
        if (isDigit(ch)) {
            let j: number = i;
            while (j < expr.length && /[0-9a-fA-FxX._]/.test(expr[j])) j++;
            out += expr.slice(i, j);
            i = j;
            continue;
        }

        // Identifier -> resolve consts to their UPPERCASE macro, keep the rest
        if (isIdentifierStart(ch)) {
            let j: number = i;
            while (j < expr.length && isIdentifierPart(expr[j])) j++;
            const id: string = expr.slice(i, j);
            if (id === 'true' || id === 'false') out += id;
            else if (isConstName(id, ctx.element, ctx.schemaService)) out += id.toUpperCase();
            else out += id; // param / subject -> bare
            i = j;
            continue;
        }

        out += ch;
        i++;
    }

    return out;
};

// Scan `expr` and return the indices of every top-level occurrence of `target`,
// skipping string literals and anything inside parentheses. For `.`, a decimal
// point (digit on both sides, e.g. `1.5`) is not a concat separator.
const findTopLevel = (expr: string, target: string): number[] => {
    const indices: number[] = [];
    let depth: number = 0;
    let i: number = 0;

    while (i < expr.length) {
        const ch: string = expr[i];

        if (ch === "'") {
            i++;
            while (i < expr.length && expr[i] !== "'") {
                if (expr[i] === '\\') i++;
                i++;
            }
            i++; // closing quote
            continue;
        }
        if (ch === '(') {
            depth++;
            i++;
            continue;
        }
        if (ch === ')') {
            depth--;
            i++;
            continue;
        }
        if (depth === 0 && ch === target) {
            if (target === '.' && isDigit(expr[i - 1]) && isDigit(expr[i + 1])) {
                i++; // decimal point, not a concat separator
                continue;
            }
            indices.push(i);
        }
        i++;
    }

    return indices;
};

// Slice `expr` at the given separator positions.
const sliceAt = (expr: string, positions: number[]): string[] => {
    const pieces: string[] = [];
    let start: number = 0;
    for (const pos of positions) {
        pieces.push(expr.slice(start, pos));
        start = pos + 1;
    }
    pieces.push(expr.slice(start));
    return pieces;
};

// Strip one layer of fully-enclosing parentheses, e.g. `(a + b)` -> `a + b`.
const unwrapParens = (expr: string): string => {
    let s: string = expr.trim();
    while (s.startsWith('(') && s.endsWith(')')) {
        // Confirm the leading '(' matches the trailing ')'
        let depth: number = 0;
        let matchesOuter: boolean = true;
        for (let i: number = 0; i < s.length; i++) {
            if (s[i] === '(') depth++;
            else if (s[i] === ')') {
                depth--;
                if (depth === 0 && i !== s.length - 1) {
                    matchesOuter = false;
                    break;
                }
            }
        }
        if (!matchesOuter) break;
        s = s.slice(1, -1).trim();
    }
    return s;
};

// Locate the single top-level comparison operator (the grammar allows at most
// one), skipping string literals and parenthesized groups. A leading `!` is
// unary, not a relop, unless it forms `!=`.
const findTopLevelComparison = (expr: string): { op: string; index: number } | null => {
    let depth: number = 0;
    let i: number = 0;
    while (i < expr.length) {
        const ch: string = expr[i];
        if (ch === "'") {
            i++;
            while (i < expr.length && expr[i] !== "'") {
                if (expr[i] === '\\') i++;
                i++;
            }
            i++;
            continue;
        }
        if (ch === '(') depth++;
        else if (ch === ')') depth--;
        else if (depth === 0) {
            const two: string = expr.slice(i, i + 2);
            if (two === '==' || two === '!=' || two === '<=' || two === '>=') return { op: two, index: i };
            if (ch === '<' || ch === '>') return { op: ch, index: i };
        }
        i++;
    }
    return null;
};

// True if a comparison operator appears at the top level (-> a BOOL operand).
const hasTopLevelComparison = (expr: string): boolean => findTopLevelComparison(expr) !== null;

// Wrap a raw `0x…` literal into an `lv_color_t` (3 digits = CSS shorthand);
// a color variable is already one and passes through.
const wrapColor = (c: string): string => {
    const m: RegExpExecArray | null = /^0x([0-9a-fA-F]+)$/.exec(c);
    if (!m) return c;
    return m[1].length === 3 ? `lv_color_hex3(${c})` : `lv_color_hex(${c})`;
};

// String `==`/`!=` -> lv_streq, color -> lv_color_eq (negated for `!=`); other
// comparisons and non-comparisons fall through to ~1:1 scalar resolution.
const resolveComparison = (expr: string, ctx: ExprContext): string => {
    const inner: string = unwrapParens(expr); // a `(cmp)` operand hides the op
    const cmp = findTopLevelComparison(inner);
    if (!cmp) return resolveScalar(expr, ctx).trim();

    const left: string = inner.slice(0, cmp.index);
    const right: string = inner.slice(cmp.index + cmp.op.length);
    // Only positive evidence picks lv_streq/lv_color_eq; an unresolved id
    // (undefined) must not, else `large == true` would become a string compare.
    const lType: ConcatType | undefined = resolveOperandType(left, ctx);
    const rType: ConcatType | undefined = resolveOperandType(right, ctx);
    const lC: string = resolveScalar(left, ctx).trim();
    const rC: string = resolveScalar(right, ctx).trim();

    if (cmp.op === '==' || cmp.op === '!=') {
        if (lType === 'string' || rType === 'string') {
            const call: string = `lv_streq(${lC}, ${rC})`;
            return cmp.op === '!=' ? `!${call}` : call;
        }
        if (lType === 'color' || rType === 'color') {
            const call: string = `lv_color_eq(${wrapColor(lC)}, ${wrapColor(rC)})`;
            return cmp.op === '!=' ? `!${call}` : call;
        }
    }

    return `${lC} ${cmp.op} ${rC}`;
};

type ConcatType = 'string' | 'int' | 'float' | 'bool' | 'color' | 'pointer';

// Map a declared LVGL type to a category. `pointer` (image / font / subject)
// allows no arithmetic; `==`/`!=` is plain pointer comparison and `!` / a bare
// reference is a NULL check — all emitted ~1:1, so no special handling needed.
const mapDeclaredType = (type: string | undefined): ConcatType => {
    switch (type) {
        case 'string':
        case 'image_src':
            return 'string';
        case 'image':
        case 'font':
        case 'subject':
            return 'pointer';
        case 'bool':
            return 'bool';
        case 'color':
            return 'color';
        case 'float':
            return 'float';
        default:
            return 'int'; // int, px, %, percentage, opa, deg, compound coordinates, ...
    }
};

// An operand's type, or `undefined` for an unresolved bare identifier.
const resolveOperandType = (operand: string, ctx: ExprContext): ConcatType | undefined => {
    const t: string = unwrapParens(operand);

    if (t.startsWith("'")) return 'string';
    if (t === 'true' || t === 'false') return 'bool';
    if (hasTopLevelComparison(t)) return 'bool';

    // Bare identifier: const, then param, then subject.
    if (/^[A-Za-z_]\w*$/.test(t)) {
        const constDef: ConstDefinitionData | undefined =
            ctx.schemaService.getConstDefinition(t) || (ctx.element?.consts ?? {})[t]?.[0].def;
        if (constDef) return constDef.type ? mapDeclaredType(constDef.type) : inferLiteralType(constDef.value);

        const param: ArgSchema | undefined = ctx.element
            ? ctx.schemaService.getElement(ctx.element.name)?.args?.find((a) => a.name === t)
            : undefined;
        if (param) return mapDeclaredType(param.type);

        const subjectType: string | undefined = Object.entries(ctx.schemaService.subjectsByType).find(([, names]) =>
            names.has(t)
        )?.[0];
        if (subjectType) return mapDeclaredType(subjectType);

        return undefined; // unresolved bare identifier
    }

    return inferLiteralType(t);
};

// Concat operand type; an unresolved identifier defaults to string.
const inferOperandType = (operand: string, ctx: ExprContext): ConcatType =>
    resolveOperandType(operand, ctx) ?? 'string';

// Infer a category from a literal / arithmetic operand (no identifiers to look up).
const inferLiteralType = (value: string): ConcatType => {
    const v: string = value.trim();
    if (v.startsWith("'")) return 'string';
    if (v === 'true' || v === 'false') return 'bool';
    if (/^0x[0-9a-fA-F]+$/.test(v)) return 'color';
    if (/\d+\.\d+/.test(v)) return 'float'; // a float literal anywhere promotes to float
    return 'int';
};

// The `lv_snprintf` format spec + argument for one concat operand.
const formatOperand = (operand: string, ctx: ExprContext): { spec: string; arg: string } => {
    const resolved: string = resolveComparison(operand, ctx);
    switch (inferOperandType(operand, ctx)) {
        case 'string':
            return { spec: '%s', arg: resolved };
        case 'bool':
            return { spec: '%s', arg: `(${resolved}) ? "true" : "false"` };
        case 'color': {
            // A raw `0x…` literal is already an int; a color variable is an
            // lv_color_t and needs converting to its RGB value.
            const rgb: string = /^0x[0-9a-fA-F]+$/.test(resolved) ? resolved : `lv_color_to_u32(${resolved})`;
            return { spec: '0x%06x', arg: `(unsigned)((${rgb}) & 0xffffff)` };
        }
        case 'float':
            // Fixed 2 decimals (needs LV_USE_FLOAT in the project).
            return { spec: '%.2f', arg: `(double)(${resolved})` };
        case 'pointer':
            return { spec: '%p', arg: `(const void *)(${resolved})` };
        case 'int':
        default:
            return { spec: '%d', arg: `(int)(${resolved})` };
    }
};

type ExportedPiece = { value: string; preface: string };

// Export one value piece (whole expression or a ternary branch): a concat
// compiles to an lv_snprintf buffer, anything else is a scalar expression.
const exportPiece = (expr: string, ctx: ExprContext): ExportedPiece => {
    const dots: number[] = findTopLevel(expr, '.');
    if (dots.length === 0) {
        const resolved: string = resolveComparison(expr, ctx);
        // A `0x…` color literal in value position must become an lv_color_t.
        const value: string = resolveOperandType(expr, ctx) === 'color' ? wrapColor(resolved) : resolved;
        return { value, preface: '' };
    }

    const operands: string[] = sliceAt(expr, dots);
    const specs: string[] = [];
    const args: string[] = [];
    for (const operand of operands) {
        const { spec, arg } = formatOperand(operand, ctx);
        specs.push(spec);
        args.push(arg);
    }

    // Buffer is declared in the function pool; here we only fill it.
    const bufName: string = nextBufName();
    const preface: string = `lv_snprintf(${bufName}, sizeof(${bufName}), "${specs.join('')}", ${args.join(', ')});\n`;
    return { value: bufName, preface };
};

/** Export a `{ ... }` expression to its C `{ value, preface }` form. */
export const exportExpression = (
    value: string,
    element: ElementData | undefined,
    schemaService: LVGLSchemaService
): { value: string; preface: string } => {
    const inner: string = value.trim().slice(1, -1).trim();
    const ctx: ExprContext = { element, schemaService };

    // Ternary `cond ? a : b`: condition is scalar, either branch may be a concat.
    const questionMarks: number[] = findTopLevel(inner, '?');
    if (questionMarks.length > 0) {
        const cond: string = inner.slice(0, questionMarks[0]);
        const rest: string = inner.slice(questionMarks[0] + 1);
        const colons: number[] = findTopLevel(rest, ':');
        if (colons.length > 0) {
            const aExpr: string = rest.slice(0, colons[0]);
            const bExpr: string = rest.slice(colons[0] + 1);

            const a: ExportedPiece = exportPiece(aExpr, ctx);
            const b: ExportedPiece = exportPiece(bExpr, ctx);
            const condC: string = resolveComparison(cond, ctx);
            return {
                value: `${condC} ? ${a.value} : ${b.value}`,
                preface: a.preface + b.preface
            };
        }
    }

    return exportPiece(inner, ctx);
};
